import { concat, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  addDays,
  addYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isFuture,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subYears,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import BasicDropdown, {
  type BasicDropdownSignature,
  type Dropdown,
} from 'ember-basic-dropdown/components/basic-dropdown';
import { modifier } from 'ember-modifier';
import { advanceSelectableOption } from 'ember-power-select/utils/group-utils';
import { or } from 'ember-truth-helpers';
import EnvoyTwoUpCalendar from 'garaje/components/envoy-two-up-calendar';
import type DateLimits from 'garaje/types/date-time/date-limits';
import {
  modifyDateInTimeZone,
  parseYyyyMmDdInTimeZone,
  sameTimeInTimeZone,
  DATE_FNS_YYYY_MM_DD,
  DEFAULT_TIMEZONE,
  type CalendarDate,
} from 'garaje/utils/date-fns-tz-utilities';
import moment from 'moment-timezone';
import { localCopy } from 'tracked-toolbox';

type DateRangeTuple = [startDate: Date, endDate: Date];

const ARROW_DOWN = 'ArrowDown';
const ARROW_UP = 'ArrowUp';
const ENTER_KEY = 'Enter';
const ESC_KEY = 'Escape';
const SPACE_KEY = ' ';
const TAB_KEY = 'Tab';

enum PresetFilters {
  today = 'Today',
  yesterday = 'Yesterday',
  past7Days = 'Past 7 days',
  past30Days = 'Past 30 days',
  past60Days = 'Past 60 days',
  past90Days = 'Past 90 days',
  monthToDate = 'Month-to-date',
  yearToDate = 'Year-to-date',
  pastYear = 'Past year',
  allTime = 'All time',
  tomorrow = 'Tomorrow',
  next2Days = 'Next 2 days',
  next7Days = 'Next 7 days',
  next30Days = 'Next 30 days',
  next60Days = 'Next 60 days',
  next90Days = 'Next 90 days',
  thisWeek = 'This week',
  thisMonth = 'This month',
  oneYearFromNow = 'One year from now',
  threeMonthsFromNow = '3 months from now',
}

type PresetFilterMin = `${keyof typeof PresetFilters}Min`;
type PresetFilterMax = `${keyof typeof PresetFilters}Max`;

type RangeFNSOptionsMax = Record<PresetFilterMax, Date>;
type RangeFNSOptionsMin = Record<PresetFilterMin, Date>;
type JoinedRangeFNSOptions = Partial<RangeFNSOptionsMax & RangeFNSOptionsMin>;

const DATE_RANGE_FNS = {
  [PresetFilters.today]: (today: Date): [Date, Date] => [today, today],
  [PresetFilters.yesterday]: (today: Date): [Date, Date] => [subDays(today, 1), subDays(today, 1)],
  [PresetFilters.past7Days]: (today: Date): [Date, Date] => [subDays(today, 7), today],
  [PresetFilters.past30Days]: (today: Date): [Date, Date] => [subDays(today, 30), today],
  [PresetFilters.past60Days]: (today: Date): [Date, Date] => [subDays(today, 60), today],
  [PresetFilters.past90Days]: (today: Date): [Date, Date] => [subDays(today, 90), today],
  [PresetFilters.monthToDate]: (today: Date, timezone: string): [Date, Date] => [
    modifyDateInTimeZone(today, timezone, startOfMonth),
    today,
  ],
  [PresetFilters.yearToDate]: (today: Date, timezone: string): [Date, Date] => [
    modifyDateInTimeZone(today, timezone, startOfYear),
    today,
  ],
  [PresetFilters.pastYear]: (today: Date): [Date, Date] => [subYears(today, 1), today],
  [PresetFilters.allTime]: (
    today: Date,
    timezone: string,
    { allTimeMin, allTimeMax }: JoinedRangeFNSOptions = {},
  ): [Date, Date] => [allTimeMin ?? parseYyyyMmDdInTimeZone('2013-01-01', new Date(), timezone), allTimeMax ?? today],
  [PresetFilters.tomorrow]: (today: Date): [Date, Date] => [addDays(today, 1), addDays(today, 1)],
  [PresetFilters.next2Days]: (today: Date): [Date, Date] => [today, addDays(today, 2)],
  [PresetFilters.next7Days]: (today: Date): [Date, Date] => [today, addDays(today, 7)],
  [PresetFilters.next30Days]: (today: Date): [Date, Date] => [today, addDays(today, 30)],
  [PresetFilters.next60Days]: (today: Date): [Date, Date] => [today, addDays(today, 60)],
  [PresetFilters.next90Days]: (today: Date): [Date, Date] => [today, addDays(today, 90)],
  [PresetFilters.thisWeek]: (today: Date, timezone: string): [Date, Date] => [
    modifyDateInTimeZone(today, timezone, startOfWeek),
    modifyDateInTimeZone(today, timezone, endOfWeek),
  ],
  [PresetFilters.thisMonth]: (today: Date, timezone: string): [Date, Date] => [
    modifyDateInTimeZone(today, timezone, startOfMonth),
    modifyDateInTimeZone(today, timezone, endOfMonth),
  ],
  [PresetFilters.oneYearFromNow]: (today: Date): [Date, Date] => [addYears(today, 1), addYears(today, 1)],
  [PresetFilters.threeMonthsFromNow]: (today: Date): [Date, Date] => [addDays(today, 90), addDays(today, 90)],
};

const DEFAULT_SHORTCUTS = [
  PresetFilters.today,
  PresetFilters.yesterday,
  PresetFilters.past7Days,
  PresetFilters.past30Days,
  PresetFilters.past60Days,
  PresetFilters.past90Days,
  PresetFilters.monthToDate,
  PresetFilters.yearToDate,
  PresetFilters.pastYear,
  PresetFilters.allTime,
];

function isValidPresetFilter(key: string): key is keyof typeof PresetFilters {
  return key in PresetFilters;
}

interface ExtendedDropdownSignature extends BasicDropdownSignature {
  Args: BasicDropdownSignature['Args'] & {
    // Additional properties used in our templates
    triggerRole?: string;
    id?: string;
    triggerClass?: string;
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DatePickerDropdown extends Dropdown {
  // Custom Public API to yield
}

export interface DatePickerDropdownSignature {
  Args: {
    rangeOptions?: JoinedRangeFNSOptions;
    dateFormat?: string;
    dateLimits?: DateLimits;
    dropdownOptions?: ExtendedDropdownSignature['Args'];
    emptyDatePlaceholder?: string;
    endDate?: Date | null;
    events?: string[];
    shortcuts?: string[];
    startDate?: Date | null;
    tabindex?: number | string;
    timezone?: string;
    onGoBackOneMonth?: (date: Date) => void;
    onGoToNextMonth?: (date: Date) => void;
    setStartDate?: (date: Date) => void;
    setEndDate?: (date: Date) => void;
    onDateSelected?: (startDate: Date, endDate: Date) => void;
    onDateRangeSelected?: (startDate: Date, endDate: Date, option?: string) => void;
    // Note: onInit and registerAPI are interface stubs for future use
    onInit?: (api: DatePickerDropdown) => void;
    registerAPI?: (api: DatePickerDropdown) => void;
    onOpen?: (api: DatePickerDropdown, e: Event | undefined) => boolean | void;
    onClose?: (api: DatePickerDropdown, e: Event | undefined) => boolean | void;
  };
  Blocks: {
    default: [];
    'date-picker-dropdown-left-sidebar': [
      {
        options: string[];
        selectedOption: string;
        highlightedOption: string | null;
        dropdownId: string;
        highlightedIndex: number;
        onOptionSelect: (option: string, close?: () => void) => void;
        onListboxFocus: () => void;
        close: () => void;
      },
    ];
  };
  Element: Element;
}

// Create a component class that extends BasicDropdown with our extended signature
const ExtendedDropdown = BasicDropdown as unknown as typeof Component<ExtendedDropdownSignature>;

export default class DatePickerDropdownComponent extends Component<DatePickerDropdownSignature> {
  storedAPI!: DatePickerDropdown;
  triggerElement!: HTMLElement;

  @tracked date: string | null = '';
  @tracked highlightedDateRange: string | null = '';

  @localCopy('args.endDate', null) endDate!: Date | null;
  @localCopy('args.startDate', null) startDate!: Date | null;
  @localCopy('args.timezone', DEFAULT_TIMEZONE) timezone!: string;
  @localCopy('args.dateFormat', 'MM/dd/yyyy') dateFormat!: string;

  get shortcuts(): string[] {
    const { shortcuts } = this.args;

    if (Array.isArray(shortcuts)) {
      return shortcuts
        .map((key) => key.trim())
        .filter(isValidPresetFilter)
        .map((key) => PresetFilters[key]);
    }

    return DEFAULT_SHORTCUTS;
  }

  get dateRangeFilterOptions(): string[] {
    const { selectedDateRange, shortcuts } = this;
    const options = [...shortcuts];

    if (!options.includes(selectedDateRange)) {
      options.push(selectedDateRange);
    }

    return options;
  }

  get selectedDateRange(): string {
    const { dateFormat, shortcuts, startDateFormat, endDateFormat, timezone } = this;

    return (
      shortcuts.find((option) => {
        const [start, end] = this.#datesForRange(option);
        const optStartFormat = formatInTimeZone(start, timezone, dateFormat);
        const optEndFormat = formatInTimeZone(end, timezone, dateFormat);

        return optStartFormat === startDateFormat && optEndFormat === endDateFormat;
      }) || 'Custom'
    );
  }

  get highlightedIndex(): number {
    return this.dateRangeFilterOptions.indexOf(this.highlightedDateRange || '');
  }

  get todayAsString(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get todayAsDate(): Date {
    return parseYyyyMmDdInTimeZone(this.todayAsString, new Date(), this.timezone);
  }

  get isToday(): boolean {
    const { dateWithDefault, startDateWithDefault, endDateWithDefault, todayAsString } = this;

    if (dateWithDefault !== todayAsString) return false;
    if (formatInTimeZone(startDateWithDefault, this.timezone, DATE_FNS_YYYY_MM_DD) !== todayAsString) return false;
    if (formatInTimeZone(endDateWithDefault, this.timezone, DATE_FNS_YYYY_MM_DD) !== todayAsString) return false;

    return true;
  }

  get isAfterToday(): boolean {
    const { startDateWithDefault, endDateWithDefault } = this;

    return isFuture(startDateWithDefault) && isFuture(endDateWithDefault);
  }

  get isSingleDay(): boolean {
    return this.startDateFormat === this.endDateFormat;
  }

  get dateWithDefault(): string {
    return this.date || formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): Date {
    if (this.startDate) return new Date(this.startDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);

    return modifyDateInTimeZone(date, this.timezone, startOfDay);
  }

  get endDateWithDefault(): Date {
    if (this.endDate) return new Date(this.endDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);

    return modifyDateInTimeZone(date, this.timezone, endOfDay);
  }

  // Calendar picker uses Moment.js which has its own timezone logic
  get startDateForCalendar(): moment.Moment {
    const tz: string = `${moment().tz() || DEFAULT_TIMEZONE}`;

    return moment(sameTimeInTimeZone(this.startDateWithDefault, tz, this.timezone));
  }

  // Calendar picker uses Moment.js which has its own timezone logic
  get endDateForCalendar(): moment.Moment {
    const tz: string = `${moment().tz() || DEFAULT_TIMEZONE}`;

    return moment(sameTimeInTimeZone(this.endDateWithDefault, tz, this.timezone));
  }

  get startDateFormat(): string {
    const { dateFormat, timezone, startDate, startDateWithDefault } = this;
    const { emptyDatePlaceholder } = this.args;

    if (emptyDatePlaceholder && !startDate) {
      return emptyDatePlaceholder;
    }

    return formatInTimeZone(startDateWithDefault, timezone, dateFormat);
  }

  get endDateFormat(): string {
    const { dateFormat, timezone, endDate, endDateWithDefault } = this;
    const { emptyDatePlaceholder } = this.args;

    if (emptyDatePlaceholder && !endDate) {
      return emptyDatePlaceholder;
    }

    return formatInTimeZone(endDateWithDefault, timezone, dateFormat);
  }

  // Use words for screen readers
  get startDateWords(): string {
    const { timezone, startDateWithDefault } = this;

    return formatInTimeZone(startDateWithDefault, timezone, 'MMMM do, yyyy');
  }

  // Use words for screen readers
  get endDateWords(): string {
    const { timezone, endDateWithDefault } = this;

    return formatInTimeZone(endDateWithDefault, timezone, 'MMMM do, yyyy');
  }

  get datesOfInterest(): moment.Moment[] {
    const { events = [] } = this.args;

    return events.map((day) => moment(day, 'YYYY-MM-DD'));
  }

  @action
  handleOpen(_api: DatePickerDropdown, e: Event | undefined): void | boolean {
    const { args } = this;

    if (args.onOpen?.(this.storedAPI, e) === false) return false;

    if (e) {
      if (e instanceof KeyboardEvent && e.type === 'keydown' && [ARROW_UP, ARROW_DOWN].includes(e.key)) {
        e.preventDefault();
      }
    }

    this.#resetHighlighted();
  }

  @action
  handleClose(_api: DatePickerDropdown, e: Event | undefined): void | boolean {
    this.args.onClose?.(this.storedAPI, e);
  }

  @action
  handleTriggerKeydown(e: KeyboardEvent): void {
    this.#routeKeydown(this.storedAPI, e);
  }

  @action
  handleListboxFocus(): void {
    this.#focusOnTriggerElement();
  }

  /**
   * handles when the user selects a start date, and an end date on the calendar
   *
   * @param startDate
   * @param endDate
   */
  @action
  didSelectDateRange(startDate: CalendarDate, endDate: CalendarDate, option?: string): void {
    this.startDate = this.#dateInTimezone(startDate);
    this.endDate = this.#dateInTimezone(endDate);
    this.date = '';

    this.args.onDateRangeSelected?.(this.startDate, this.endDate, option);
  }

  /**
   * handles when the user selects one of the preset filters
   *
   * @param startDate
   * @param endDate
   */
  @action
  didSelectDate(startDate: CalendarDate, endDate: CalendarDate): void {
    this.args.onDateSelected?.(this.#dateInTimezone(startDate), this.#dateInTimezone(endDate));

    this.#resetHighlighted();
    this.#focusOnTriggerElement();
  }

  @action
  onDateRangeSelect(option: string, close?: () => void): void {
    if (option === 'Custom') return;

    const [start, end] = this.#datesForRange(option);

    this.didSelectDateRange(start, end, option);

    close?.();
  }

  @action
  setStartDate(date: CalendarDate): void {
    this.startDate = this.#dateInTimezone(date);

    this.args.setStartDate?.(this.startDate);
  }

  @action
  setEndDate(date: CalendarDate): void {
    this.endDate = this.#dateInTimezone(date);

    this.args.setEndDate?.(this.endDate);
  }

  @action
  handleGoBackOneMonth(date: Date): void {
    this.args.onGoBackOneMonth?.(new Date(date));
  }

  @action
  handleGoToNextMonth(date: Date): void {
    this.args.onGoToNextMonth?.(new Date(date));
  }

  #dateInTimezone(date: CalendarDate): Date {
    const tz: string = date instanceof moment ? `${moment().tz() || DEFAULT_TIMEZONE}` : this.timezone;
    const plainDate: Date = date instanceof moment ? (date as moment.Moment).toDate() : (date as Date);

    return sameTimeInTimeZone(plainDate, this.timezone, tz);
  }

  #datesForRange(option: string): DateRangeTuple {
    const { todayAsDate, timezone } = this;
    const [start, end] = DATE_RANGE_FNS[<PresetFilters>option](todayAsDate, timezone, this.args.rangeOptions);

    // Compute start and end of day last to reduce issues with Daylight Saving
    return [modifyDateInTimeZone(start, timezone, startOfDay), modifyDateInTimeZone(end, timezone, endOfDay)];
  }

  #registerAPI(triggerElement: HTMLElement, [publicAPI]: [DatePickerDropdown]): void {
    this.triggerElement = triggerElement;
    this.storedAPI = publicAPI;
  }

  // Keyboard interaction managed by "trigger" element.
  // Restore focus to maintain keyboard event handling.
  #focusOnTriggerElement(): void {
    this.triggerElement?.focus();
  }

  #resetHighlighted(): void {
    this.#highlight(this.selectedDateRange);
  }

  #highlight(opt: string): void {
    this.highlightedDateRange = opt;
  }

  #routeKeydown(api: DatePickerDropdown, e: KeyboardEvent): boolean | void {
    const { key } = e;

    if ([ARROW_UP, ARROW_DOWN].includes(key)) {
      return this.#handleKeyUpDown(api, e);
    }

    if ([ENTER_KEY].includes(key)) {
      return this.#handleKeyEnter(api, e);
    }

    if ([ESC_KEY].includes(key)) {
      return this.#handleKeyEsc(api, e);
    }

    if ([SPACE_KEY].includes(key)) {
      return this.#handleKeySpace(api, e);
    }

    if ([TAB_KEY].includes(key)) {
      return this.#handleKeyTab(api, e);
    }
  }

  #handleKeySpace(api: DatePickerDropdown, e: KeyboardEvent): void {
    if (e.target !== null && ['TEXTAREA', 'INPUT'].includes((e.target as Element).nodeName)) {
      e.stopImmediatePropagation();
    } else if (api.isOpen && this.highlightedDateRange) {
      e.preventDefault(); // Prevents scrolling of the page.
      e.stopImmediatePropagation();
      this.onDateRangeSelect(this.highlightedDateRange);
      api.actions.close(e);
    }
  }

  #handleKeyUpDown(api: DatePickerDropdown, e: KeyboardEvent): void {
    if (api.isOpen) {
      e.preventDefault();
      e.stopPropagation();

      const { dateRangeFilterOptions, highlightedDateRange } = this;
      const step: 1 | -1 = e.key === ARROW_DOWN ? 1 : -1;
      const newHighlighted = <string | null>advanceSelectableOption(dateRangeFilterOptions, highlightedDateRange, step);

      this.highlightedDateRange = newHighlighted;
    } else {
      api.actions.open(e);
    }
  }

  #handleKeyEnter(api: DatePickerDropdown, e: KeyboardEvent): boolean | void {
    if (api.isOpen && this.highlightedDateRange) {
      this.onDateRangeSelect(this.highlightedDateRange);
      api.actions.close(e);
      e.stopImmediatePropagation();

      return false;
    }
  }

  #handleKeyTab(api: DatePickerDropdown, e: KeyboardEvent): void {
    api.actions.close(e);
  }

  #handleKeyEsc(api: DatePickerDropdown, e: KeyboardEvent): void {
    api.actions.close(e);
  }

  updateRegisterAPI = modifier((triggerElement: Element, [publicAPI]: [DatePickerDropdown]) => {
    this.#registerAPI(triggerElement as HTMLElement, [publicAPI]);
  });

  <template>
    <ExtendedDropdown
      @calculatePosition={{@dropdownOptions.calculatePosition}}
      @destination={{@dropdownOptions.destination}}
      @disabled={{@dropdownOptions.disabled}}
      @horizontalPosition={{@dropdownOptions.horizontalPosition}}
      @initiallyOpened={{@dropdownOptions.initiallyOpened}}
      @preventScroll={{or @dropdownOptions.preventScroll false}}
      @renderInPlace={{@dropdownOptions.renderInPlace}}
      @rootEventType={{@dropdownOptions.rootEventType}}
      @verticalPosition={{@dropdownOptions.verticalPosition}}
      @onClose={{this.handleClose}}
      @onOpen={{this.handleOpen}}
      @triggerRole={{@dropdownOptions.triggerRole}}
      as |dropdown|
    >
      <dropdown.Trigger
        id={{@dropdownOptions.id}}
        class={{concat
          "envoy-date-picker-dropdown inline-block flex-row text-base cursor-pointer border border-solid border-carbon-20 px-2 py-2 bg-white rounded-md focus:ring-2 focus:ring-gem-50 "
          (if @dropdownOptions.disabled "readonly !bg-carbon-5 border border-[#e9e9ea] !cursor-default ")
          (if @dropdownOptions.triggerClass @dropdownOptions.triggerClass)
        }}
        data-test-date-picker-dropdown-trigger
        aria-activedescendant={{if dropdown.isOpen (concat dropdown.uniqueId "-" this.highlightedIndex)}}
        aria-haspopup="listbox"
        role={{or @dropdownOptions.triggerRole "button"}}
        {{on "keydown" this.handleTriggerKeydown}}
        {{this.updateRegisterAPI dropdown}}
        ...attributes
      >
        <span class="not-sr-only" aria-hidden="true">
          <span data-test-start-date>{{this.startDateFormat}}</span>
          {{#unless this.isSingleDay}}
            <span class="mx-2">&#x2192;</span>
            <span data-test-end-date>{{this.endDateFormat}}</span>
          {{/unless}}
        </span>

        {{! Use words for screen readers }}
        <span class="sr-only">
          <span data-test-start-date-sr>{{this.startDateWords}}</span>
          {{#unless this.isSingleDay}}
            <span class="mx-2">through</span>
            <span data-test-end-date-sr>{{this.endDateWords}}</span>
          {{/unless}}
        </span>
      </dropdown.Trigger>

      <dropdown.Content class="flex bg-white rounded-md shadow-xl">
        {{#if (has-block "date-picker-dropdown-left-sidebar")}}
          <div class="relative w-44 border-r" data-test-date-picker-sidebar>
            {{yield
              (hash
                options=this.dateRangeFilterOptions
                selectedOption=this.selectedDateRange
                highlightedOption=this.highlightedDateRange
                dropdownId=dropdown.uniqueId
                highlightedIndex=this.highlightedIndex
                onOptionSelect=this.onDateRangeSelect
                onListboxFocus=this.handleListboxFocus
                close=dropdown.actions.close
              )
              to="date-picker-dropdown-left-sidebar"
            }}
          </div>
        {{/if}}

        <div class="relative" data-test-date-picker-dropdown-calendar>
          <EnvoyTwoUpCalendar
            class="date-picker-dropdown-calendar"
            @noShadow={{true}}
            @inline={{true}}
            @didSelectDateRange={{this.didSelectDate}}
            @dateRangeSelected={{this.didSelectDateRange}}
            @startDate={{this.startDateForCalendar}}
            @endDate={{this.endDateForCalendar}}
            @setStartDate={{this.setStartDate}}
            @setEndDate={{this.setEndDate}}
            @handleGoBackOneMonth={{this.handleGoBackOneMonth}}
            @handleGoToNextMonth={{this.handleGoToNextMonth}}
            @events={{this.datesOfInterest}}
            @dateLimits={{@dateLimits}}
          />
        </div>
      </dropdown.Content>
    </ExtendedDropdown>
  </template>
}
